import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useLang } from "@/context/LanguageContext";
import { SUPPORTED_LANGS } from "@/lib/i18n";
import api from "@/lib/api";
import useEntitlement from "@/hooks/useEntitlement";
import KeyData from "@/components/KeyData";
import { ChevronRight, Moon, Sun, LogOut, Languages, PenLine, BookText, Star, FileDown, Compass, Shield, Scroll, UserCog, Trash2, Sparkles } from "lucide-react";
import { useState } from "react";
import DeleteAccountModal from "@/components/DeleteAccountModal";

export default function Menu() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { lang, setLang, t } = useLang();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const ent = useEntitlement();

  const trialDaysLeft = user?.trial_ends_at ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / (1000*60*60*24))) : null;

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const cycleLang = async () => {
    const idx = SUPPORTED_LANGS.findIndex(l => l.code === lang);
    const next = SUPPORTED_LANGS[(idx + 1) % SUPPORTED_LANGS.length];
    setLang(next.code);
    try { await api.put("/me/preferences", { language: next.code }); } catch { /* silent */ }
  };
  const currentLang = SUPPORTED_LANGS.find(l => l.code === lang) || SUPPORTED_LANGS[0];

  const downloadJournalPdf = async () => {
    try {
      const r = await api.get("/journal/export.pdf", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vedari-journal-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (e) {
      console.error("PDF export failed", e);
    }
  };

  return (
    <div className="animate-gentle-rise">
      <h1 className="font-display text-[36px] leading-tight" data-testid="menu-title">{t("menu.title")}</h1>

      <section className="vedari-card mt-6 flex items-center gap-4" data-testid="menu-profile">
        {user?.picture ? (
          <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full object-cover" style={{ border: "1.5px solid var(--vedari-divider)" }} />
        ) : (
          <div className="w-14 h-14 rounded-full flex items-center justify-center font-serif text-h2" style={{ backgroundColor: "var(--vedari-divider)", color: "var(--vedari-gold)" }}>
            {user?.name?.[0] || "·"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-serif text-h3 truncate">{user?.name || "—"}</p>
          <p className="text-body-sm truncate" style={{ color: "var(--vedari-ink-soft)" }}>{user?.email}</p>
        </div>
      </section>

      <Section title={lang === "hi" ? "व्यक्तिगतकरण" : "Personalization"}>
        <ClickRow
          icon={<UserCog size={18} strokeWidth={1.5} />}
          label={lang === "hi" ? "मेरा विवरण संपादित करें" : "Edit my details"}
          sub={lang === "hi" ? "नाम, प्राथमिकताएँ, जीवन-पत्र, जन्म विवरण" : "Name, priorities, life letter, birth details"}
          onClick={() => navigate("/app/profile/edit")}
          testid="menu-edit-profile"
        />
        <ClickRow
          icon={<Star size={18} strokeWidth={1.5} />}
          label={lang === "hi" ? "मेरी कुंडली" : "My Kundli"}
          sub={lang === "hi" ? "आपकी जन्म कुंडली का सार" : "Your birth chart at a glance"}
          onClick={() => navigate("/app/kundli")}
          testid="menu-kundli"
        />
        <ClickRow
          icon={<PenLine size={18} strokeWidth={1.5} />}
          label={lang === "hi" ? "मेरा जीवन-पत्र" : "My Life Letter"}
          sub={lang === "hi" ? "Vedari आपके जीवन के बारे में अभी क्या जानता है" : "What Vedari knows about your life right now"}
          onClick={() => navigate("/app/life-letter")}
          testid="menu-life-letter"
        />
      </Section>

      <Section title={t("menu.subscription")}>
        <SubscriptionRows ent={ent} t={t} lang={lang} trialDaysLeft={trialDaysLeft} navigate={navigate} />
      </Section>

      <Section title={t("menu.preferences")}>
        <Row
          label={<span className="flex items-center gap-3">{theme === "dark" ? <Moon size={18} strokeWidth={1.5}/> : <Sun size={18} strokeWidth={1.5}/>} {t("menu.theme")}</span>}
          right={
            <button onClick={toggle} data-testid="menu-theme-toggle"
              className="px-3 py-1 rounded-full text-caption font-medium"
              style={{ border: "1.5px solid var(--vedari-gold)", color: "var(--vedari-gold)" }}>
              {theme === "dark" ? t("menu.theme_dark") : t("menu.theme_light")}
            </button>
          }
        />
        <Row
          label={<span className="flex items-center gap-3"><Languages size={18} strokeWidth={1.5}/> {t("menu.language")}</span>}
          right={
            <button onClick={cycleLang} data-testid="menu-language-toggle"
              className="px-3 py-1 rounded-full text-caption font-medium"
              style={{ border: "1.5px solid var(--vedari-gold)", color: "var(--vedari-gold)" }}>
              {currentLang.native}
            </button>
          }
        />
        <Row label={t("menu.notifications")} right={<span style={{ color: "var(--vedari-ink-soft)" }}>{t("menu.email_only")}</span>} testid="menu-notifications" />
      </Section>

      <Section title={t("menu.privacy")}>
        <ClickRow
          icon={<FileDown size={18} strokeWidth={1.5} />}
          label={lang === "hi" ? "मेरी डायरी PDF में निर्यात करें" : "Export my journal as PDF"}
          sub={lang === "hi" ? "एक सुंदर PDF आपके सभी प्रविष्टियों का" : "A keepsake PDF of every entry"}
          onClick={downloadJournalPdf}
          testid="menu-pdf-export"
        />
        <ClickRow
          icon={<Trash2 size={18} strokeWidth={1.5} />}
          label={t("menu.delete")}
          sub={lang === "hi" ? "स्थायी रूप से। 30 दिनों के भीतर पूरा होता है।" : "Permanently. Completed within 30 days."}
          onClick={() => setDeleteOpen(true)}
          testid="menu-delete"
          danger
        />
      </Section>

      <Section title={t("menu.about")}>
        <ClickRow
          icon={<BookText size={18} strokeWidth={1.5} />}
          label={lang === "hi" ? "वैदिक शब्दकोश" : "Vedic Glossary"}
          sub={lang === "hi" ? "रास्ते में मिलने वाले संस्कृत शब्द" : "Sanskrit terms you'll meet as you walk"}
          onClick={() => navigate("/app/glossary")}
          testid="menu-glossary"
        />
        <ClickRow
          icon={<Compass size={18} strokeWidth={1.5} />}
          label={lang === "hi" ? "हमारा दृष्टिकोण" : "Our approach"}
          sub={lang === "hi" ? "Vedari क्या है, क्या नहीं" : "What Vedari is and is not"}
          onClick={() => navigate("/app/approach")}
          testid="menu-approach"
        />
        <ClickRow
          icon={<Shield size={18} strokeWidth={1.5} />}
          label={lang === "hi" ? "गोपनीयता" : "Privacy"}
          sub={lang === "hi" ? "क्या लेते हैं, क्यों लेते हैं" : "What we collect and why"}
          onClick={() => navigate("/app/privacy")}
          testid="menu-privacy"
        />
        <ClickRow
          icon={<Scroll size={18} strokeWidth={1.5} />}
          label={lang === "hi" ? "सेवा की शर्तें" : "Terms of service"}
          sub={lang === "hi" ? "सरल भाषा। कोई जाल नहीं।" : "Plain language. No traps."}
          onClick={() => navigate("/app/terms")}
          testid="menu-terms"
        />
        <Row label={t("menu.version")} right={<KeyData>0.3.0</KeyData>} testid="menu-version" />
      </Section>

      <button onClick={doLogout} className="vedari-btn-secondary w-full mt-8 mb-12 inline-flex items-center justify-center gap-2" data-testid="menu-logout-btn">
        <LogOut size={18} strokeWidth={1.5} /> {t("menu.signout")}
      </button>

      <DeleteAccountModal open={deleteOpen} onClose={() => setDeleteOpen(false)} />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <p className="text-caption uppercase tracking-widest mb-3 px-1" style={{ color: "var(--vedari-ink-soft)" }}>{title}</p>
      <div className="vedari-card divide-y" style={{ padding: 0 }}>
        {children}
      </div>
    </section>
  );
}

function Row({ label, right, testid }) {
  return (
    <div className="flex items-center justify-between px-5 py-4" style={{ borderColor: "var(--vedari-divider)" }} data-testid={testid}>
      <span className="text-body">{label}</span>
      <span className="text-body-sm">{right}</span>
    </div>
  );
}

function ClickRow({ icon, label, sub, onClick, testid, danger }) {
  return (
    <button onClick={onClick} data-testid={testid} className="w-full flex items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-[rgba(184,149,74,0.04)]" style={{ borderColor: "var(--vedari-divider)" }}>
      <span style={{ color: danger ? "var(--vedari-rust)" : "var(--vedari-gold)" }}>{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-body" style={{ color: danger ? "var(--vedari-rust)" : "var(--vedari-ink)" }}>{label}</p>
        {sub && <p className="text-caption mt-0.5" style={{ color: "var(--vedari-ink-soft)" }}>{sub}</p>}
      </div>
      <ChevronRight size={18} style={{ color: danger ? "var(--vedari-rust)" : "var(--vedari-ink-soft)" }} />
    </button>
  );
}

function formatDate(iso, lang) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return null; }
}

function SubscriptionRows({ ent, t, lang, trialDaysLeft, navigate }) {
  if (ent.loading) {
    return <Row label={t("menu.plan")} right={<span style={{ color: "var(--vedari-ink-soft)" }}>…</span>} testid="menu-plan" />;
  }
  const source = ent.data?.source;
  const sku = ent.data?.subscription_sku;
  const periodEnd = formatDate(ent.data?.subscription_current_period_end, lang);
  const autoRenew = ent.data?.subscription_auto_renewing;

  // Reviewer access — short circuit
  if (source === "reviewer") {
    return (
      <>
        <Row label={t("menu.plan")} right={t("menu.plan_reviewer")} testid="menu-plan" />
      </>
    );
  }

  // Active subscription — show plan name, renewal date, manage link
  if (source === "subscription") {
    const planLabel = sku === "vedari_yearly" ? t("menu.plan_active_yearly") : t("menu.plan_active_monthly");
    return (
      <>
        <Row label={t("menu.plan")} right={<span style={{ color: "var(--vedari-gold)" }}>{planLabel}</span>} testid="menu-plan" />
        {periodEnd && (
          <Row
            label={autoRenew ? t("menu.renews_on") : t("menu.ends_on")}
            right={<KeyData>{periodEnd}</KeyData>}
            testid="menu-renews"
          />
        )}
        <ClickRow
          icon={<Sparkles size={18} strokeWidth={1.5} />}
          label={t("menu.manage_play")}
          sub={sku || ""}
          onClick={() => {
            // Deep-link to the Play Store subscription management screen
            const pkg = "life.vedari.twa";
            const url = sku
              ? `https://play.google.com/store/account/subscriptions?sku=${encodeURIComponent(sku)}&package=${pkg}`
              : `https://play.google.com/store/account/subscriptions?package=${pkg}`;
            window.open(url, "_blank", "noopener");
          }}
          testid="menu-manage-sub"
        />
      </>
    );
  }

  // Trial active
  if (source === "trial") {
    return (
      <>
        <Row label={t("menu.plan")} right={t("menu.plan_value")} testid="menu-plan" />
        {trialDaysLeft !== null && (
          <Row
            label={t("menu.trial_ends")}
            right={<><KeyData>{trialDaysLeft}</KeyData> {t("today.days")}</>}
            testid="menu-trial"
          />
        )}
        <ClickRow
          icon={<Sparkles size={18} strokeWidth={1.5} />}
          label={t("menu.see_plans")}
          sub={lang === "hi" ? "मासिक ₹190 · वार्षिक ₹1899" : "Monthly ₹190 · Yearly ₹1899"}
          onClick={() => navigate("/app/subscribe")}
          testid="menu-upgrade"
        />
      </>
    );
  }

  // Expired / none
  return (
    <>
      <Row
        label={t("menu.plan")}
        right={<span style={{ color: "var(--vedari-rust)" }}>{t("menu.plan_expired")}</span>}
        testid="menu-plan"
      />
      <ClickRow
        icon={<Sparkles size={18} strokeWidth={1.5} />}
        label={t("menu.subscribe_now")}
        sub={lang === "hi" ? "मासिक ₹190 · वार्षिक ₹1899" : "Monthly ₹190 · Yearly ₹1899"}
        onClick={() => navigate("/app/subscribe")}
        testid="menu-upgrade"
      />
    </>
  );
}
